import {reduce, forEach, omit, sortBy} from 'lodash';
import { getCookie, setCookie } from './cookie';
import { EXPERIMENT_COOKIE_NAME, NINETY_DAYS_IN_S } from '../constants';

/*
 * Takes in an array of variant objects.
 * Picks one at random based on their probability percentages.
 */
const pickVariantUsingRNG = (variants) => {
  const rand = Math.random();
  let currentBucket = 0;
  return variants.find(({ percentage }) => {
    currentBucket += percentage / 100;
    return rand < currentBucket;
  });
};

const parseExperimentsCookie = (cookie) => {
  try {
    return JSON.parse(cookie);
  } catch (e) {
    /*
     * Malformed JSON in cookie breaks JSON.parse() -- this happens for some
     * crawlers. Returns a blank array of r29 experiment data in this case.
     *
     * See: https://sentry.prod.rf29.net/app/rosetta_node_prod/group/879841/
     */
    return [];
  }
};

/*
 * Returns a comma delimited sorted list of all experiment IDs.
 * Used for comparison of the experiments data in the API vs the cookies.
 */
const getExperimentIds = (experiments) =>
  experiments.map(({ id }) => id).sort().join();

/*
 * This method returns an object containing each experiment and its chosen
 * variant for this user. It removes all other data from the original experiment
 * objects so that we are left with just the experiment name, id,
 * customDimension. It then assigns chosenVariant.
 *
 * Checks cookies to see if the user already has a chosenVariant set for that
 * experiment. If the user does not, we pick a variant for them with RNG. If
 * they do, we continue to use that variant.
 *
 * Does not set any cookies.
 */
export function setVariantsForExperiments(cookies, experiments) {
  return reduce(experiments, (output, exp) => {
    output[exp.name] = omit(exp, ['variants', 'name', 'dateCreated']);

    const existingCookie = cookies[EXPERIMENT_COOKIE_NAME];
    let existingCookieValue;
    if (existingCookie) {
      const parsedCookie = parseExperimentsCookie(existingCookie);
      existingCookieValue = parsedCookie.find(({ id }) =>
        id === exp.id
      );
    }

    output[exp.name].chosenVariant = existingCookieValue
      ? existingCookieValue.chosenVariant
      : pickVariantUsingRNG(exp.variants).name;
    return output;
  }, {});
}

/*
 * Called on the client-side. This uses the experiments key in the Redux store.
 * The cookies that get set here are read later by setVariantsForExperiments.
 */
export const setExperimentCookies = (store) => {
  const experiments = store.getState().experiments;
  const existingCookie = getCookie(EXPERIMENT_COOKIE_NAME);
  let shouldNotSetCookie = false;

  if (existingCookie) {
    const parsedCookie = parseExperimentsCookie(existingCookie);
    shouldNotSetCookie = getExperimentIds(parsedCookie) === getExperimentIds(Object.values(experiments));
  }

  if (shouldNotSetCookie) {
    return;
  }

  const cookieValue = [];
  forEach(experiments, ({ id, chosenVariant }, name) =>
    cookieValue.push({ id, name, chosenVariant })
  );

  setCookie({
    name: EXPERIMENT_COOKIE_NAME,
    value: JSON.stringify(sortBy(cookieValue, 'name')),
    expirationInSeconds: NINETY_DAYS_IN_S
  });
};
